import { useEffect, useRef, useState } from 'react';
import type { ColorPalette } from '@vidmakr/bindings/types';
import { MAX_PALETTE_COLORS, STARTER_PALETTE, normalizePaletteColors, parseHexList } from '@vidmakr/shared';
import { Palette, Plus, Trash2, X } from 'lucide-react';
import { Button, Card, ConfirmModal, Input, Textarea } from './ui';
import { useOrgPalettes } from '@/lib/hooks';
import { reducers } from '@/lib/stdb';
import { attempt } from '@/lib/toast';

const PASTE_HINT = 'Paste hex codes: #0f172a #38bdf8, f472b6; one per line…';

/** Swatches of what pasted text holds, and the pieces that are not colours. */
function PastePreview({ text }: { text: string }) {
  const { colors, skipped } = parseHexList(text);
  if (!text.trim()) return null;
  return (
    <div className="mt-1.5 space-y-1" data-paste-preview>
      <div className="flex flex-wrap items-center gap-1">
        {colors.map((c) => (
          <span key={c} className="h-5 w-5 rounded border border-line" style={{ background: c }} title={c} />
        ))}
        <span className="ml-1 text-xs text-muted">
          {colors.length} colour{colors.length === 1 ? '' : 's'}
          {colors.length >= MAX_PALETTE_COLORS ? ` (the most a palette holds)` : ''}
        </span>
      </div>
      {skipped.length ? <div className="text-xs text-warn">Not colours, left out: {skipped.slice(0, 8).join(' ')}{skipped.length > 8 ? ' …' : ''}</div> : null}
    </div>
  );
}

/** A new palette from a name and pasted hex codes (the starter colours when none are pasted). */
function NewPalette({ orgId, defaultName, onDone }: { orgId: string; defaultName: string; onDone: () => void }) {
  const [name, setName] = useState(defaultName);
  const [text, setText] = useState('');
  const colors = parseHexList(text).colors;
  const create = async () => {
    if (await attempt(() => reducers().createColorPalette({ id: '', orgId, name, colors: colors.length ? colors : STARTER_PALETTE }), 'Palette created')) onDone();
  };
  return (
    <div className="mb-3 rounded-lg border border-accent/50 p-3" data-new-palette>
      <Input className="mb-2 h-8 text-sm font-medium" value={name} placeholder="Palette name" onChange={(e) => setName(e.target.value)} autoFocus />
      <Textarea rows={3} className="font-mono text-xs" placeholder={PASTE_HINT} value={text} onChange={(e) => setText(e.target.value)} />
      <PastePreview text={text} />
      <div className="mt-2 flex justify-end gap-2">
        <Button size="sm" variant="ghost" onClick={onDone}>
          Cancel
        </Button>
        <Button size="sm" variant="primary" disabled={!name.trim() || (!!text.trim() && !colors.length)} onClick={() => void create()}>
          {colors.length ? `Create with ${colors.length} colour${colors.length === 1 ? '' : 's'}` : 'Create'}
        </Button>
      </div>
    </div>
  );
}

function PaletteRow({ palette, canManage }: { palette: ColorPalette; canManage: boolean }) {
  const [name, setName] = useState(palette.name);
  const [colors, setColors] = useState(palette.colors);
  const [paste, setPaste] = useState('');
  const [deleting, setDeleting] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout>>();
  useEffect(() => setName(palette.name), [palette.name]);
  useEffect(() => setColors(palette.colors), [palette.colors]);

  const save = (next: { name?: string; colors?: string[] }, delay = 0) => {
    if (next.colors) setColors(next.colors);
    clearTimeout(timer.current);
    const run = () => void attempt(() => reducers().updateColorPalette({ id: palette.id, name: next.name ?? name, colors: next.colors ?? colors }));
    if (delay) timer.current = setTimeout(run, delay);
    else run();
  };
  const full = colors.length >= MAX_PALETTE_COLORS;
  const pasted = parseHexList(paste).colors;

  return (
    <div className="rounded-lg border border-line p-3" data-palette={palette.id}>
      <div className="mb-2 flex items-center gap-2">
        <Input
          className="h-8 flex-1 text-sm font-medium"
          value={name}
          disabled={!canManage}
          onChange={(e) => setName(e.target.value)}
          onBlur={() => name.trim() !== palette.name && save({ name })}
          onKeyDown={(e) => e.key === 'Enter' && (e.target as HTMLInputElement).blur()}
        />
        {canManage ? (
          <Button size="sm" variant="ghost" title="Delete palette" onClick={() => setDeleting(true)}>
            <Trash2 size={14} />
          </Button>
        ) : null}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {colors.map((c, i) => (
          <div key={`${i}-${c}`} className="group relative">
            <label className="block h-9 w-9 cursor-pointer rounded-md border border-line shadow-sm" style={{ background: c }} title={c}>
              <input
                type="color"
                className="sr-only"
                value={c}
                disabled={!canManage}
                onChange={(e) => save({ colors: colors.map((x, j) => (j === i ? e.target.value : x)) }, 400)}
              />
            </label>
            {canManage ? (
              <button
                className="absolute -right-1.5 -top-1.5 hidden h-4 w-4 items-center justify-center rounded-full bg-panel-2 text-muted shadow group-hover:flex hover:text-fg"
                title="Remove colour"
                onClick={() => save({ colors: colors.filter((_, j) => j !== i) })}
              >
                <X size={10} />
              </button>
            ) : null}
            <div className="mt-0.5 text-center font-mono text-[9px] text-faint">{c.slice(1)}</div>
          </div>
        ))}
        {canManage && !full ? (
          <button
            className="mb-3.5 flex h-9 w-9 items-center justify-center rounded-md border border-dashed border-line text-muted hover:text-fg"
            title="Add colour"
            onClick={() => save({ colors: [...colors, colors[colors.length - 1] ?? '#ffffff'] })}
          >
            <Plus size={14} />
          </button>
        ) : null}
      </div>
      {canManage ? (
        <div className="mt-2">
          <Textarea rows={1} className="min-h-7 font-mono text-xs" placeholder={PASTE_HINT} value={paste} onChange={(e) => setPaste(e.target.value)} />
          <PastePreview text={paste} />
          {pasted.length ? (
            <div className="mt-1.5 flex justify-end gap-2">
              <Button size="sm" disabled={full} title={full ? `A palette holds up to ${MAX_PALETTE_COLORS} colours` : undefined} onClick={() => (save({ colors: normalizePaletteColors([...colors, ...pasted]) }), setPaste(''))}>
                Add to palette
              </Button>
              <Button size="sm" onClick={() => (save({ colors: pasted }), setPaste(''))}>
                Replace colours
              </Button>
            </div>
          ) : null}
        </div>
      ) : null}
      <ConfirmModal
        open={deleting}
        onClose={() => setDeleting(false)}
        title="Delete palette"
        danger
        confirmLabel="Delete"
        message={`"${palette.name}" is removed for everyone in the organization. Clips keep their colours.`}
        onConfirm={async () => void (await attempt(() => reducers().deleteColorPalette({ id: palette.id }), 'Palette deleted'))}
      />
    </div>
  );
}

/** Organization settings: the brand colour palettes members pick from in the editor. */
export function ColorPalettes({ orgId, canManage }: { orgId: string; canManage: boolean }) {
  const palettes = useOrgPalettes(orgId);
  const [adding, setAdding] = useState(false);
  let defaultName = 'Brand colours';
  for (let n = 2; palettes.some((p) => p.name === defaultName); n++) defaultName = `Brand colours ${n}`;
  return (
    <Card className="p-5">
      <div className="mb-1 flex items-center justify-between">
        <h2 className="flex items-center gap-2 font-semibold">
          <Palette size={16} className="text-muted" /> Colour palettes
        </h2>
        {canManage ? (
          <Button size="sm" disabled={adding} onClick={() => setAdding(true)}>
            <Plus size={14} /> New palette
          </Button>
        ) : null}
      </div>
      <p className="mb-4 text-xs text-faint">
        Brand colours for everyone in the organization: they show next to every colour field in the editor (text, shapes, gradients…).
        {canManage ? '' : ' Only admins can change them.'}
      </p>
      {adding ? <NewPalette orgId={orgId} defaultName={defaultName} onDone={() => setAdding(false)} /> : null}
      {palettes.length ? (
        <div className="space-y-3">
          {palettes.map((p) => (
            <PaletteRow key={p.id} palette={p} canManage={canManage} />
          ))}
        </div>
      ) : adding ? null : (
        <div className="text-sm text-muted">No palettes yet.</div>
      )}
    </Card>
  );
}
